<template>
  <div class="app-table" :class="{ 'app-table--light': lightMode }">
    <v-data-table
      class="app-table__table"
      v-bind="bindings"
      :page.sync="page"
      :items-per-page="perPage"
      hide-default-footer
      v-on="$listeners"
      @pagination="pagination = $event"
    >
      <!-- 슬롯 투명 전달 (item.xxx / header.xxx / no-data 등). Vue2.6+ $scopedSlots가 일반 슬롯도 포함 -->
      <template v-for="(_, name) in $scopedSlots" #[name]="slotData"><slot :name="name" v-bind="slotData"></slot></template>
    </v-data-table>

    <!-- 자체 푸터: 기본 푸터의 셀렉트 드롭다운이 루트 테마를 따라가는 문제 → AppSelect로 직접 렌더(테마 완전 통제) -->
    <div v-if="!hideFooter" class="app-table__footer">
      <span class="app-table__fp-label">페이지당 행</span>
      <AppSelect class="app-table__fp-select" :items="perPageItems" :value="perPage" :height="24" @input="onPerPage"></AppSelect>
      <!-- numbered: 전통 숫자 페이지네이션 (1 2 3 …) -->
      <template v-if="numbered">
        <v-pagination
          v-model="page"
          class="app-table__fp-pages"
          :length="(pagination && pagination.pageCount) || 1"
          :total-visible="7"
        ></v-pagination>
      </template>
      <template v-else>
        <span class="app-table__fp-range">{{ rangeText }}</span>
        <button class="app-table__fp-btn" :disabled="page <= 1" @click="page -= 1">
          <v-icon size="17">mdi-chevron-left</v-icon>
        </button>
        <button class="app-table__fp-btn" :disabled="!pagination || page >= pagination.pageCount" @click="page += 1">
          <v-icon size="17">mdi-chevron-right</v-icon>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import Themeable from "vuetify/lib/mixins/themeable";
import AppSelect from "./AppSelect.vue";

// v-data-table 래퍼 — App* 톤. 테마는 Themeable 자동 상속(내부 v-data-table·AppSelect도 provide로 따라옴).
//  모든 prop/이벤트/슬롯 투명 전달. 기본값: dense. 페이지네이션 푸터는 자체 렌더(AppSelect).
//  hide-default-footer를 넘기면 자체 푸터도 숨김(footless 테이블).
export default {
  name: "AppDataTable",
  components: { AppSelect },
  mixins: [Themeable],
  inheritAttrs: false,
  props: {
    itemsPerPageOptions: { type: Array, default: () => [5, 10, 20, 50] },
    // 전통 숫자 페이지네이션(1 2 3 …) 사용 — 기본은 범위+화살표
    numbered: { type: Boolean, default: false },
  },
  data() {
    const attrs = this.$attrs;
    const initPer = Number(attrs["items-per-page"] || attrs.itemsPerPage) || 10;
    return { page: 1, perPage: initPer, pagination: null };
  },
  computed: {
    lightMode() {
      return !this.isDark;
    },
    bindings() {
      // 페이지 상태는 래퍼가 관리 → 관련 attr 제외하고 전달
      const { "items-per-page": _a, itemsPerPage: _b, page: _c, "hide-default-footer": _d, hideDefaultFooter: _e, ...rest } = this.$attrs;
      return { dense: true, ...rest };
    },
    hideFooter() {
      return this.$attrs["hide-default-footer"] !== undefined || this.$attrs.hideDefaultFooter !== undefined;
    },
    perPageItems() {
      return this.itemsPerPageOptions.map((n) => ({ text: n === -1 ? "전체" : String(n), value: n }));
    },
    rangeText() {
      const p = this.pagination;
      if (!p || !p.itemsLength) return "0 / 0";
      return `${p.pageStart + 1}-${p.pageStop} / ${p.itemsLength}`;
    },
  },
  methods: {
    onPerPage(v) {
      this.perPage = v;
      this.page = 1;
    },
  },
};
</script>

<style scoped>
/* ── 컨테이너 ── */
.app-table {
  background: #1c1c26;
  border: 1px solid #2a2a38;
  border-radius: 10px;
  overflow: hidden;
}
.app-table__table {
  background: transparent !important;
}

/* ── 헤더 ── */
.app-table ::v-deep thead th {
  background: #15151d !important;
  color: #9a93ad !important;
  font-size: 11px !important;
  font-weight: 600;
  letter-spacing: 0.2px;
  border-bottom: 1px solid #2a2a38 !important;
  white-space: nowrap;
}
.app-table ::v-deep thead th.sortable:hover,
.app-table ::v-deep thead th.active {
  color: #cfc8e6 !important;
}
.app-table ::v-deep thead th .v-data-table-header__icon {
  color: #8f7ddb !important;
}

/* ── 바디 ── */
.app-table ::v-deep tbody td {
  font-size: 12px !important;
  color: #dcdce6;
  border-bottom: 1px solid #23232f !important;
}
.app-table ::v-deep tbody tr:hover {
  background: rgba(145, 85, 253, 0.07) !important;
}
.app-table ::v-deep tbody tr.v-data-table__selected {
  background: rgba(145, 85, 253, 0.14) !important;
}
.app-table ::v-deep .v-data-table__empty-wrapper td {
  color: #6b6b7d !important;
}

/* ── 자체 푸터 ── */
.app-table__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 6px 10px;
  border-top: 1px solid #2a2a38;
  font-size: 11.5px;
  color: #9a93ad;
}
.app-table__fp-label {
  white-space: nowrap;
}
.app-table__fp-select {
  max-width: 88px;
}
.app-table__fp-range {
  margin-left: 4px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.app-table__fp-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  cursor: pointer;
}
.app-table__fp-btn .v-icon {
  color: #9a93ad !important;
}
.app-table__fp-btn:hover:not(:disabled) {
  background: rgba(145, 85, 253, 0.12);
}
.app-table__fp-btn:hover:not(:disabled) .v-icon {
  color: #b79cff !important;
}
.app-table__fp-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

/* 숫자 페이지네이션 (v-pagination) — App* 톤 컴팩트 */
.app-table__fp-pages {
  margin-left: 4px;
}
.app-table__fp-pages ::v-deep .v-pagination__item,
.app-table__fp-pages ::v-deep .v-pagination__navigation {
  height: 24px;
  min-width: 24px;
  margin: 0 2px;
  font-size: 11.5px;
  border-radius: 6px;
  box-shadow: none;
  background: transparent;
  color: #9a93ad;
}
.app-table__fp-pages ::v-deep .v-pagination__navigation .v-icon {
  font-size: 16px;
  color: #9a93ad;
}
.app-table__fp-pages ::v-deep .v-pagination__item:hover,
.app-table__fp-pages ::v-deep .v-pagination__navigation:hover {
  background: rgba(145, 85, 253, 0.12);
}
.app-table__fp-pages ::v-deep .v-pagination__item--active {
  background: #7c4dff !important;
  color: #fff;
}
.app-table__fp-pages ::v-deep .v-pagination__navigation--disabled {
  opacity: 0.35;
}

/* ── light theme ── */
.app-table--light {
  background: #fff;
  border-color: #e2e0ec;
}
.app-table--light ::v-deep thead th {
  background: #f8f7fb !important;
  color: #8a84a0 !important;
  border-bottom-color: #e9e7f2 !important;
}
.app-table--light ::v-deep thead th.sortable:hover,
.app-table--light ::v-deep thead th.active {
  color: #4b4462 !important;
}
.app-table--light ::v-deep thead th .v-data-table-header__icon {
  color: #7c4dff !important;
}
.app-table--light ::v-deep tbody td {
  color: #37324a;
  border-bottom-color: #f0eef6 !important;
}
.app-table--light ::v-deep tbody tr:hover {
  background: rgba(124, 77, 255, 0.05) !important;
}
.app-table--light ::v-deep tbody tr.v-data-table__selected {
  background: rgba(124, 77, 255, 0.09) !important;
}
.app-table--light ::v-deep .v-data-table__empty-wrapper td {
  color: #a29cb8 !important;
}
.app-table--light .app-table__footer {
  border-top-color: #e9e7f2;
  color: #8a84a0;
}
.app-table--light .app-table__fp-btn .v-icon {
  color: #a29cb8 !important;
}
.app-table--light .app-table__fp-btn:hover:not(:disabled) {
  background: rgba(124, 77, 255, 0.07);
}
.app-table--light .app-table__fp-btn:hover:not(:disabled) .v-icon {
  color: #7c4dff !important;
}
.app-table--light .app-table__fp-pages ::v-deep .v-pagination__item,
.app-table--light .app-table__fp-pages ::v-deep .v-pagination__navigation {
  color: #8a84a0;
  background: transparent;
}
.app-table--light .app-table__fp-pages ::v-deep .v-pagination__navigation .v-icon {
  color: #8a84a0;
}
.app-table--light .app-table__fp-pages ::v-deep .v-pagination__item:hover,
.app-table--light .app-table__fp-pages ::v-deep .v-pagination__navigation:hover {
  background: rgba(124, 77, 255, 0.07);
}
.app-table--light .app-table__fp-pages ::v-deep .v-pagination__item--active {
  background: #7c4dff !important;
  color: #fff;
}
</style>
